import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  ShoppingCart, Search, X, DollarSign, MessageSquare,
  Loader2, CheckCircle2, Lock, Sparkles,
} from 'lucide-react';
import { SPRITES, RARITIES, VARIANTS } from '../mock';
import { buildPriceMap } from '../lib/pricing';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';

export default function BuyDialog({ open, onOpenChange }) {
  const { isAuthed, openAuth } = useAuth();
  const [overrides, setOverrides] = useState([]);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null); // sprite object
  const [offer, setOffer] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Fetch admin price overrides (public endpoint)
  useEffect(() => {
    if (!open) return;
    api.getCatalog().then(setOverrides).catch(() => setOverrides([]));
  }, [open]);

  const priceMap = useMemo(() => buildPriceMap(overrides), [overrides]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return SPRITES.filter((s) => {
      const avail = priceMap[s.id]?.available !== false;
      if (!avail) return false;
      if (!q) return true;
      return (
        s.name.toLowerCase().includes(q) ||
        s.rarity.toLowerCase().includes(q) ||
        s.variant.toLowerCase().includes(q)
      );
    }).slice(0, 60);
  }, [search, priceMap]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setSearch(''); setPicked(null); setOffer(''); setMessage('');
      setSubmitting(false); setSubmitted(false); setError('');
    }
  }, [open]);

  // Default offer to suggested price when picking
  useEffect(() => {
    if (picked) {
      const p = priceMap[picked.id]?.price ?? 5;
      setOffer(String(p));
    }
  }, [picked, priceMap]);

  const submit = async () => {
    if (!picked) return;
    const amount = parseFloat(offer);
    if (!isFinite(amount) || amount < 0) {
      setError('Enter a valid offer amount');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const suggested = priceMap[picked.id]?.price ?? 0;
      await api.createOffer({
        sprite_id: picked.id,
        sprite_name: picked.name,
        sprite_img: picked.img,
        sprite_rarity: picked.rarity,
        sprite_variant: picked.variant,
        offer_amount: amount,
        suggested_price: suggested,
        message: message.trim(),
      });
      setSubmitted(true);
      toast({
        title: 'Offer sent!',
        description: 'You\'ll get a notification when the dev responds.',
      });
    } catch (e) {
      setError(e.message || 'Failed to send offer');
    } finally {
      setSubmitting(false);
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="buy-dialog"
        className="bg-[#fff7d6] border-[3px] border-[#3a1064] rounded-2xl shadow-[0_6px_0_#3a1064] max-w-2xl p-0 overflow-hidden max-h-[92vh] flex flex-col"
      >
        <div
          className="px-5 py-4 border-b-[3px] border-[#3a1064]"
          style={{ background: 'linear-gradient(135deg,#ffd900,#ff2bd6 50%,#9d3dff)' }}
        >
          <DialogHeader className="text-left space-y-1">
            <DialogTitle className="text-white font-black text-2xl flex items-center gap-2 drop-shadow-[0_2px_0_#3a1064]">
              <ShoppingCart className="h-6 w-6" /> Buy directly from the devs
            </DialogTitle>
            <DialogDescription className="text-white/95 font-bold drop-shadow-[0_1px_0_#3a1064]">
              Anonymous. No waiting. Skip the trade queue — pick what you want, name your price.
            </DialogDescription>
          </DialogHeader>
        </div>

        {!isAuthed ? (
          <div className="p-8 text-center">
            <Lock className="h-12 w-12 mx-auto text-[#9d3dff]" />
            <div className="font-black text-[#3a1064] text-xl mt-3">Sign in to buy</div>
            <p className="text-[#3a1064]/80 font-bold text-sm mt-1 max-w-sm mx-auto">
              You need a Sprite Vault account so the devs can reply to your offer
              with a payment link &amp; meet-up details.
            </p>
            <Button
              data-testid="buy-signin-btn"
              onClick={() => { onOpenChange(false); openAuth('register'); }}
              className="mt-5 h-11 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064] px-5"
            >
              Create a free account
            </Button>
          </div>
        ) : submitted ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500" />
            <div className="font-black text-[#3a1064] text-2xl mt-3">Offer sent!</div>
            <p className="text-[#3a1064]/80 font-bold text-sm mt-2 max-w-sm mx-auto">
              The devs will review your ${parseFloat(offer).toFixed(2)} offer for{' '}
              <span className="text-[#ff2bd6]">{picked?.name}</span>. You&apos;ll get an
              in-app notification when they respond (usually within 24h).
            </p>
            <Button
              data-testid="buy-done-btn"
              onClick={() => onOpenChange(false)}
              className="mt-5 h-11 rounded-xl border-2 border-[#3a1064] bg-[#3a1064] hover:bg-[#2a0848] text-white font-black shadow-[0_3px_0_#ffd900] px-6"
            >
              Done
            </Button>
          </div>
        ) : !picked ? (
          // Step 1 — pick a sprite
          <div className="flex flex-col flex-1 min-h-0">
            <div className="px-5 pt-4 pb-2 border-b-2 border-[#3a1064]/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3a1064]/50" />
                <Input
                  data-testid="buy-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, rarity, or variant…"
                  className="pl-10 h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064]"
                />
              </div>
            </div>
            <div
              data-testid="buy-sprite-list"
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 grid grid-cols-2 sm:grid-cols-3 gap-2"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {filtered.map((s) => {
                const r = RARITIES[s.rarity];
                const v = VARIANTS[s.variant];
                const price = priceMap[s.id]?.price ?? 0;
                return (
                  <button
                    key={s.id}
                    data-testid={`buy-pick-${s.id}`}
                    onClick={() => setPicked(s)}
                    className="bg-white rounded-xl border-2 border-[#3a1064] p-2 hover:bg-yellow-50 hover:-translate-y-0.5 transition-transform shadow-[0_2px_0_#3a1064] text-left"
                  >
                    <div className="flex items-center gap-2">
                      <img src={s.img} alt="" className="h-12 w-12 object-contain shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-black text-[#3a1064] text-xs truncate">{s.name}</div>
                        <div className="flex gap-1 mt-1 flex-wrap">
                          <span className="text-[8px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: r.color }}>{r.label}</span>
                          <span className="text-[8px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: v.color }}>{v.label}</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between">
                      <span className="text-[10px] font-black text-[#3a1064]/60 uppercase">From</span>
                      <span className="font-black text-[#3a1064] text-base">${price.toFixed(2)}</span>
                    </div>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div className="col-span-full text-center text-[#3a1064]/60 font-bold py-8">
                  No sprites match. Try another search.
                </div>
              )}
            </div>
          </div>
        ) : (
          // Step 2 — submit offer
          <div className="p-5 space-y-4 overflow-y-auto">
            <div className="bg-white rounded-xl border-2 border-[#3a1064] p-3 flex items-center gap-3 shadow-[0_3px_0_#3a1064]">
              <img src={picked.img} alt="" className="h-16 w-16 object-contain shrink-0 bg-yellow-50 rounded-lg border-2 border-[#3a1064] p-1" />
              <div className="flex-1 min-w-0">
                <div className="font-black text-[#3a1064] text-lg truncate">{picked.name}</div>
                <div className="flex gap-1 mt-1 flex-wrap">
                  <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded text-white" style={{ background: RARITIES[picked.rarity].color }}>{RARITIES[picked.rarity].label}</span>
                  <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded text-white" style={{ background: VARIANTS[picked.variant].color }}>{VARIANTS[picked.variant].label}</span>
                </div>
                <div className="text-xs font-bold text-[#3a1064]/70 mt-1">
                  Suggested: <span className="text-[#3a1064] font-black">${(priceMap[picked.id]?.price ?? 0).toFixed(2)}</span>
                </div>
              </div>
              <button
                data-testid="buy-change-pick-btn"
                onClick={() => setPicked(null)}
                className="h-9 px-2 rounded-lg border-2 border-[#3a1064] bg-white hover:bg-yellow-50 font-black text-xs text-[#3a1064] flex items-center gap-1"
              >
                <X className="h-3.5 w-3.5" /> Change
              </button>
            </div>

            <div>
              <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5 text-sm">
                <DollarSign className="h-4 w-4" /> Your offer (USD)
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3a1064] font-black text-lg">$</span>
                <Input
                  data-testid="buy-offer-amount-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={offer}
                  onChange={(e) => setOffer(e.target.value)}
                  className="pl-7 h-12 rounded-xl border-2 border-[#3a1064] font-black text-[#3a1064] text-lg"
                />
              </div>
              <p className="text-[10px] font-bold text-[#3a1064]/60 mt-1">
                Offer any amount — even below suggested. The devs review every offer.
              </p>
            </div>

            <div>
              <Label className="text-[#3a1064] font-black flex items-center gap-1.5 mb-1.5 text-sm">
                <MessageSquare className="h-4 w-4" /> Message <span className="text-[#3a1064]/50 font-bold ml-1">(optional)</span>
              </Label>
              <Textarea
                data-testid="buy-message-input"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Any details? Preferred platform, time, etc."
                className="rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064] resize-none"
                maxLength={500}
              />
            </div>

            {error && (
              <div className="bg-rose-100 border-2 border-rose-400 rounded-lg px-3 py-2 text-rose-800 font-bold text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center gap-2 bg-yellow-100 border-2 border-[#ffd900] rounded-lg px-3 py-2">
              <Sparkles className="h-4 w-4 text-[#ff2bd6] shrink-0" />
              <p className="text-xs font-bold text-[#3a1064]">
                Anonymous transaction. We only see your account email.
              </p>
            </div>

            <Button
              data-testid="buy-submit-offer-btn"
              onClick={submit}
              disabled={submitting}
              className="w-full h-12 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064] text-base"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending offer…</>
              ) : (
                <><ShoppingCart className="h-4 w-4 mr-2" /> Send offer for ${parseFloat(offer || '0').toFixed(2)}</>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
